import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import AdminSidebar from "../components/AdminSidebar";
import { toast } from "sonner";
import {
  Users, Gift, Clock, TrendingUp, UserCheck, UserPlus, RefreshCw, Shield, X, Check
} from "lucide-react";

const AdminAmbassadors = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renewModal, setRenewModal] = useState(null);
  const [renewForm, setRenewForm] = useState({ action: "ambassador", duration_months: 3, discount_percent: 50 });

  useEffect(() => {
    if (user?.role !== "admin") { navigate("/"); return; }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/ambassadors`, { credentials: "include" });
      if (res.ok) setData(await res.json());
      else toast.error("Failed to load data");
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const handleRenew = async (userId) => {
    try {
      const body = renewForm.action === "ambassador"
        ? { access_type: "ambassador", duration_months: renewForm.duration_months }
        : { access_type: "discount", discount_percent: renewForm.discount_percent, duration_months: renewForm.duration_months };

      const res = await fetch(`${API}/admin/users/${userId}/special-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success(renewForm.action === "ambassador" ? "Ambassador renewed!" : "Discount granted!");
        setRenewModal(null);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed");
      }
    } catch { toast.error("Failed to update"); }
  };

  const handleRevoke = async (userId) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/special-access`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Access revoked"); fetchData(); }
    } catch { toast.error("Failed to revoke"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian">
        <AdminSidebar />
        <main className="lg:ml-64 p-6 pt-20 lg:pt-6">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  const s = data?.summary || {};

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-ambassadors">
      <AdminSidebar />
      <main className="lg:ml-64 min-h-screen px-4 sm:px-6 pb-6 pt-20 lg:pt-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-1">
                AMBASSADOR <span className="text-crimson">PROGRAM</span>
              </h1>
              <p className="text-gray-400 text-sm">Track ambassador performance and manage access</p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#333] text-white">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { label: "Total Ambassadors", value: s.total_ambassadors || 0, icon: Users, color: "text-white" },
              { label: "Active", value: s.active_ambassadors || 0, icon: Shield, color: "text-emerald-400" },
              { label: "Expired", value: s.expired_ambassadors || 0, icon: Clock, color: "text-red-400" },
              { label: "Total Referrals", value: s.total_referrals || 0, icon: UserPlus, color: "text-crimson" },
              { label: "Active Referrals", value: s.total_active_referrals || 0, icon: UserCheck, color: "text-blue-400" },
            ].map((stat) => (
              <div key={stat.label} className="bg-obsidian-light border border-[#222] p-4" data-testid={`admin-stat-${stat.label.toLowerCase().replace(/\s/g, "-")}`}>
                <stat.icon className={`w-4 h-4 ${stat.color} mb-1`} />
                <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Ambassadors Table */}
          {data?.ambassadors?.length > 0 ? (
            <div className="bg-obsidian-light border border-[#222] overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#333]">
                    <th className="text-left p-4 text-gray-400 text-xs uppercase">Ambassador</th>
                    <th className="text-left p-4 text-gray-400 text-xs uppercase">Status</th>
                    <th className="text-left p-4 text-gray-400 text-xs uppercase">Referrals</th>
                    <th className="text-left p-4 text-gray-400 text-xs uppercase">Active Users</th>
                    <th className="text-left p-4 text-gray-400 text-xs uppercase">Duration</th>
                    <th className="text-left p-4 text-gray-400 text-xs uppercase">Expires</th>
                    <th className="text-right p-4 text-gray-400 text-xs uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ambassadors.map((amb) => (
                    <tr key={amb.user_id} className="border-b border-[#1a1a1a] hover:bg-white/5" data-testid={`ambassador-row-${amb.user_id}`}>
                      <td className="p-4">
                        <div className="text-sm text-white font-medium">{amb.name || "No name"}</div>
                        <div className="text-xs text-gray-500">{amb.email}</div>
                        {amb.promo_code && <div className="text-[10px] text-gray-600 font-mono mt-0.5">{amb.promo_code}</div>}
                      </td>
                      <td className="p-4">
                        {amb.is_active ? (
                          <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 uppercase">Active</span>
                        ) : (
                          <span className="bg-red-500/20 text-red-400 text-xs px-2 py-0.5 uppercase">Expired</span>
                        )}
                      </td>
                      <td className="p-4 text-white font-bold">{amb.total_referrals}</td>
                      <td className="p-4 text-emerald-400 font-bold">{amb.active_referrals}</td>
                      <td className="p-4 text-sm text-gray-300">{amb.duration_months}mo</td>
                      <td className="p-4">
                        {amb.is_active ? (
                          <div>
                            <div className="text-sm text-white">{amb.days_remaining}d left</div>
                            <div className="text-[10px] text-gray-500">{amb.expires_at ? new Date(amb.expires_at).toLocaleDateString() : ""}</div>
                          </div>
                        ) : (
                          <span className="text-xs text-red-400">Expired</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!amb.is_active || amb.expired ? (
                            <Button
                              size="sm"
                              className="bg-crimson hover:bg-crimson/80 text-white text-xs"
                              onClick={() => { setRenewModal(amb); setRenewForm({ action: "ambassador", duration_months: 3, discount_percent: 50 }); }}
                              data-testid={`renew-${amb.user_id}`}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" /> Renew
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs"
                              onClick={() => handleRevoke(amb.user_id)}
                              data-testid={`revoke-${amb.user_id}`}
                            >
                              <X className="w-3 h-3 mr-1" /> Revoke
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-obsidian-light border border-[#222] p-12 text-center">
              <Gift className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold mb-2">No Ambassadors Yet</h3>
              <p className="text-gray-400 text-sm mb-4">Grant ambassador access from User Management</p>
              <Button onClick={() => navigate("/admin/users")} className="bg-crimson hover:bg-crimson/80">
                Go to Users
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Renew/Convert Modal */}
      {renewModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6 w-full max-w-sm"
            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold">Renew Access</h3>
              <button onClick={() => setRenewModal(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-gray-400 mb-4">{renewModal.name} ({renewModal.email})</p>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Access Type</label>
                <select
                  value={renewForm.action}
                  onChange={(e) => setRenewForm(f => ({ ...f, action: e.target.value }))}
                  className="w-full px-3 py-2 bg-obsidian border border-[#333] text-white text-sm"
                  data-testid="renew-type"
                >
                  <option value="ambassador">Renew Ambassador (Free)</option>
                  <option value="discount">Convert to Discount</option>
                </select>
              </div>

              {renewForm.action === "discount" && (
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Discount %</label>
                  <input
                    type="number" min="1" max="99"
                    value={renewForm.discount_percent}
                    onChange={(e) => setRenewForm(f => ({ ...f, discount_percent: parseInt(e.target.value) || 50 }))}
                    className="w-full px-3 py-2 bg-obsidian border border-[#333] text-white text-sm"
                    data-testid="renew-discount"
                  />
                </div>
              )}

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Duration</label>
                <select
                  value={renewForm.duration_months}
                  onChange={(e) => setRenewForm(f => ({ ...f, duration_months: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 bg-obsidian border border-[#333] text-white text-sm"
                  data-testid="renew-duration"
                >
                  <option value={1}>1 Month</option>
                  <option value={3}>3 Months</option>
                  <option value={6}>6 Months</option>
                  <option value={12}>1 Year</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={() => setRenewModal(null)} variant="outline" className="flex-1 border-[#333] text-white">Cancel</Button>
              <Button onClick={() => handleRenew(renewModal.user_id)} className="flex-1 bg-crimson hover:bg-crimson/80" data-testid="confirm-renew">
                <Check className="w-4 h-4 mr-1" /> Confirm
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminAmbassadors;
